import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';

// Auth & Services
import { authService } from './utils/auth/authService';


// Error Handling
import { ErrorBoundary } from './components/errors/ErrorBoundary';

// Components
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import DeleteAccount from './components/DeleteAccount';
import PublicVendorRegistration from './components/PublicVendorRegistration';
import VendorOnboardingForm from './components/VendorOnboardingForm';
import UserOnboardingForm from './components/UserOnboardingForm';
import LoginScreen from './components/auth/LoginScreen';

// Apps
import PumpersPortal from './apps/PumpersPortal';
import ManagerPortal from './apps/ManagerPortal';
import VendorPortal from './apps/VendorPortal';
import InvestorPortal from './apps/InvestorPortal';
import SuperAdminPortal from './apps/SuperAdminPortal';

export default function App() {
  // Routes check
  const pathname = window.location.pathname;

  // Static Routes (Public)
  if (pathname === '/terms' || pathname === '/terms-of-service') return <TermsOfService />;
  if (pathname === '/privacy-policy' || pathname === '/privacy') return <PrivacyPolicy />;
  if (pathname === '/delete-account' || pathname === '/delete' || pathname === '/account/delete') return <DeleteAccount />;
  if (pathname === '/vendor/register' || pathname === '/vendors/apply') return <PublicVendorRegistration />;
  if (pathname.startsWith('/vendor/onboard/')) {
    const token = pathname.replace('/vendor/onboard/', '');
    return <VendorOnboardingForm token={token} />;
  }
  if (pathname.startsWith('/user/onboard/')) {
    const token = pathname.replace('/user/onboard/', '');
    return <UserOnboardingForm token={token} />;
  }

  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewAsField, setViewAsField] = useState(false);

  // Initialize Auth
  useEffect(() => {
    const initAuth = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Auth init error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);

  // Handlers
  const handleLogin = (userData: any) => {
    setUser(userData);
    setIsAuthenticated(true);
    // authService.login already saves the session
    toast.success(`Welcome back, ${userData.firstName || userData.name}!`);
  };

  const handleLogout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setViewAsField(false);
    toast.message('Signed out successfully');
  };

  // Loading Screen
  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated || !user) {
    return (
      <>
        <Toaster position="top-center" />
        <LoginScreen onLoginSuccess={handleLogin} />
      </>
    );
  }

  // ROUTING LOGIC

  // SuperAdmin Portal
  if (user.role === 'superadmin') {
    return (
      <ErrorBoundary>
        <SuperAdminPortal user={user} onLogout={handleLogout} />
      </ErrorBoundary>
    );
  }

  // Manager Role - Command Center
  if (user.role === 'manager' && !viewAsField) {
    return (
      <ErrorBoundary>
        <Toaster position="top-right" />
        <ManagerPortal
          user={user}
          onLogout={handleLogout}
          onUserUpdate={setUser}
          onSwitchToFieldView={() => {
            toast.info("Switching to Field View...");
            setViewAsField(true);
          }}
        />
      </ErrorBoundary>
    );
  }

  // Vendor Portal
  if (user.role === 'vendor') {
    return (
      <ErrorBoundary>
        <Toaster position="top-right" />
        <VendorPortal user={user} onLogout={handleLogout} />
      </ErrorBoundary>
    );
  }

  // Investor Portal
  if (user.role === 'investor') {
    return (
      <ErrorBoundary>
        <Toaster position="top-right" />
        <InvestorPortal user={user} onLogout={handleLogout} />
      </ErrorBoundary>
    );
  }

  // Pumper/Field User (or Manager viewing as Field)
  // PumpersPortal (legacy FieldApp) checks its own auth internally as well, which is redundant but safe.
  return (
    <ErrorBoundary>
      <Toaster position="top-right" />
      <PumpersPortal
        viewAsField={viewAsField}
        onReturnToCommandCenter={() => setViewAsField(false)}
      />
    </ErrorBoundary>
  );
}
